import type {
  Estimate,
  EstimateLineItem,
  EstimateMembershipOffer,
  EstimateMembershipOfferLineAdjustment,
} from '@/types/estimate';
import {
  isMembershipPurchaseLine as isMembershipLine,
  withMembershipSetupFees,
} from '@notifi/shared-types/membership-line';
import { computeEstimatePricingPreview } from './estimate-pricing-preview';
import type { TaxResolutionResult } from '@/types/tax';
import { offerCountedLines, splitOptionalOffers } from './service-components';

type MembershipPlanSnapshot = Estimate['membershipPlanSnapshot'];

/**
 * NOT-625 — does this persisted `membershipPlanSnapshot` say the document was
 * MEMBER-priced?
 *
 * FE mirror of the server predicate (operations
 * sales/estimates/support/membership-offer.ts `isMemberPricedSnapshot`) — the
 * tells must stay identical on both sides. The snapshot has two tenants: the
 * member-pricing snapshot (active member or in-cart join — carries
 * membershipId / planName / memberships[] / a real totalDiscount /
 * lineDiscounts) and, for a NON-member, an offer-only `{ offer }` wrapper.
 *
 * ONE predicate for every surface that decides whether to show the
 * join-and-save upsell. The quote page and the PDF each had their own partial
 * copy, and they disagreed on a legacy `{ membershipId, offer }` snapshot: the
 * page hid the offer while the downloaded/signature PDF printed it — telling
 * an existing member to join.
 */
export function isMemberPricedSnapshot(
  snapshot: MembershipPlanSnapshot | null | undefined,
): boolean {
  if (!snapshot) return false;
  if (snapshot.membershipId) return true;
  if (snapshot.planName) return true;
  if ((snapshot.memberships ?? []).length > 0) return true;
  if ((snapshot.totalDiscount ?? 0) > 0) return true;
  return (snapshot.lineDiscounts ?? []).length > 0;
}

/**
 * The offer this snapshot entitles the customer to SEE: a valid frozen offer
 * with a non-negative saving on a document that is not member-priced. A $0
 * saving remains actionable because an explicitly-added plan can be valuable
 * for visits, waivers, or other non-discount benefits.
 * The server only writes an offer for a non-member document; the member-priced
 * check is the defensive belt for legacy snapshots the fallback paths carried
 * forward. Callers layer their own surface-specific actionability gates on top.
 */
export function membershipOfferFromSnapshot(
  snapshot: MembershipPlanSnapshot | null | undefined,
): EstimateMembershipOffer | null {
  const offer = snapshot?.offer ?? null;
  if (!offer || !Number.isFinite(offer.savings) || offer.savings < 0) return null;
  // Keep the public CTA aligned with the approval endpoint. Legacy snapshots
  // do not freeze every material plan term, so the server intentionally
  // rejects them instead of selling a potentially changed membership.
  if (!/^[a-f0-9]{64}$/.test(offer.planTermsFingerprint ?? '')) return null;
  if (!isMemberPricedSnapshot(snapshot)) return offer;
  // Member-priced: an existing member of ANOTHER plan may still take this
  // promotion (the server priced it as the increment over the rates they
  // already hold). Once the document is priced by the offered plan itself —
  // the customer accepted this join-and-save — the offer stays off, as it
  // does for a legacy snapshot that cannot say which plan priced it.
  const pricedPlanIds = snapshotPricedPlanIds(snapshot);
  if (pricedPlanIds.length === 0) return null;
  return pricedPlanIds.includes(offer.planId) ? null : offer;
}

/**
 * The plans a member-priced snapshot was priced by. Empty for a legacy snapshot
 * that names no plan (only `membershipId` / `planName`), which is therefore
 * indistinguishable from an accepted join-and-save.
 */
function snapshotPricedPlanIds(snapshot: MembershipPlanSnapshot | null | undefined): string[] {
  return (snapshot?.memberships ?? [])
    .map((row) => row?.planId)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/** Use one of the server-frozen billing choices without recomputing plan pricing. */
export function membershipOfferForBillingOption(
  offer: EstimateMembershipOffer | null,
  billingOptionId?: string | null,
): EstimateMembershipOffer | null {
  if (!offer) return null;
  const choices = offer.billingOptions ?? [];
  if (choices.length === 0) {
    return !billingOptionId || billingOptionId === offer.billingOptionId ? offer : null;
  }
  const selected = choices.find(
    (choice) => choice.id === (billingOptionId ?? offer.billingOptionId),
  );
  if (!selected) return null;
  return {
    ...offer,
    planPrice: selected.price,
    planPriceInterval: selected.intervalUnit,
    planPriceIntervalValue: selected.intervalValue,
    billingOptionId: selected.id,
    billingOptionLabel: selected.label,
  };
}

/** Allocate the document discount over this selection, never over declined add-ons. */
function frozenMembershipSelectionContext(
  offer: EstimateMembershipOffer,
  selectedLines: readonly EstimateLineItem[],
  allLines: readonly EstimateLineItem[],
) {
  const context = offer.selectionPricing!;
  const selected = new Set(selectedLines);
  const flags = context.lines.map((row) => {
    const source = row.lineKey
      ? allLines.find((line) => line.lineKey === row.lineKey)
      : allLines[row.lineIndex];
    return Boolean(source && selected.has(source));
  });
  const group = context.taxGroup;
  const taxResolution: TaxResolutionResult | null = group
    ? {
        exempt: false,
        resolvedFrom: 'MANUAL',
        group: {
          ...group,
          orgId: '',
          isDefault: false,
          isActive: true,
          source: 'SNAPSHOT',
          items: group.items.map((item, index) => ({
            ...item,
            id: String(index),
            taxCodeId: String(index),
            taxCode: {
              ...item.taxCode,
              id: String(index),
              name: item.taxCode.name ?? '',
              appliesTo: item.taxCode.appliesTo ?? ['all'],
              jurisdiction: item.taxCode.jurisdiction ?? null,
              jurisdictionType: item.taxCode.jurisdictionType ?? null,
              effectiveRate: item.taxCode.rate,
              rateOverride: null,
              isOverridden: false,
            },
          })),
        },
      }
    : null;
  const price = (member: boolean, selectedFlags: boolean[]) =>
    computeEstimatePricingPreview({
      lineItems: context.lines.map((row, index) => ({
        ...row,
        _rowId: String(row.lineIndex),
        quantity: 1,
        basePrice: member ? row.memberNet : row.originalNet,
        laborRate: 0,
        costBasis: 0,
        markupPercent: 0,
        priceStrategy: 'FLAT',
        isSelected: selectedFlags[index]!,
      })),
      taxResolution,
      pricingMode: 'STANDARD',
      membershipInfo: null,
      discountType: context.documentDiscount.type === 'PERCENT' ? '%' : '$',
      discountValue: context.documentDiscount.value,
    });
  return { price, flags };
}

/** The nonmember side of the frozen offer, using the same discount/tax engine
 * as the member comparison. Used when optional scope changes before approval. */
export function membershipOfferOriginalSelectionPricing(
  offer: EstimateMembershipOffer | null,
  selectedLines: readonly EstimateLineItem[],
  allLines: readonly EstimateLineItem[],
) {
  if (!offer?.selectionPricing) return null;
  const { price, flags } = frozenMembershipSelectionContext(offer, selectedLines, allLines);
  return price(false, flags);
}

function repriceFrozenMembershipSelection(
  offer: EstimateMembershipOffer,
  selectedLines: readonly EstimateLineItem[],
  allLines: readonly EstimateLineItem[],
): EstimateMembershipOffer {
  const context = offer.selectionPricing!;
  const selected = new Set(selectedLines);
  const { price, flags } = frozenMembershipSelectionContext(offer, selectedLines, allLines);
  const original = price(false, flags);
  const member = price(true, flags);
  const previews = new Map<
    number,
    { original: ReturnType<typeof price>; member: ReturnType<typeof price> }
  >();
  for (const group of splitOptionalOffers([...allLines]).offers) {
    if (selected.has(group.parent) || isMembershipLine(group.parent)) continue;
    const added = new Set(offerCountedLines(group, true));
    const indexes = context.lines.flatMap((row, index) => {
      const source = row.lineKey
        ? allLines.find((line) => line.lineKey === row.lineKey)
        : allLines[row.lineIndex];
      return source && added.has(source) ? [index] : [];
    });
    const groupFlags = flags.map((on, index) => on || indexes.includes(index));
    const preview = { original: price(false, groupFlags), member: price(true, groupFlags) };
    for (const index of indexes) previews.set(index, preview);
  }
  const money = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
  const lineAdjustments = context.lines
    .map((row, index) => {
      // Unticked cards preview only their own sale group on top of the current
      // selection. Selected rows all receive the current header allocation.
      const preview = previews.get(index);
      if (!flags[index] && !preview) {
        return (
          offer.lineAdjustments?.find((adjustment) => adjustment.lineIndex === row.lineIndex) ??
          null
        );
      }
      const before = (preview?.original ?? original).lineItemPreviews[index]!;
      const after = (preview?.member ?? member).lineItemPreviews[index]!;
      return {
        lineIndex: row.lineIndex,
        lineKey: row.lineKey ?? null,
        lineItemId: row.lineItemId,
        originalSubtotal: row.subtotal,
        memberSubtotal: row.subtotal,
        originalTax: before.lineTax,
        memberTax: after.lineTax,
        originalTotal: before.lineTotal,
        memberTotal: after.lineTotal,
        savings: money(before.lineTotal - after.lineTotal),
      };
    })
    .filter((row): row is EstimateMembershipOfferLineAdjustment => row !== null);
  return {
    ...offer,
    lineAdjustments,
    nonMemberGrandTotal: original.grandTotal,
    memberGrandTotal: member.grandTotal,
    savings: money(original.grandTotal - member.grandTotal),
  };
}

/**
 * Rebase a frozen offer onto the add-ons the customer currently has selected.
 *
 * The snapshot stores tax-aware adjustments per line. That is enough to remove
 * a declined optional line from both sides of the comparison without hiding
 * the membership offer. A newly selected legacy line may not have a frozen
 * adjustment; in that case it stays at its quoted price in this preview and
 * the approval endpoint performs the authoritative reprice (which can only
 * improve the member total if that line is eligible).
 */
export function membershipOfferForSelectedLines(
  offer: EstimateMembershipOffer | null,
  lines: readonly EstimateLineItem[],
  allLines: readonly EstimateLineItem[] | null | undefined,
): EstimateMembershipOffer | null {
  if (!offer) return null;
  if (offer.selectionPricing) {
    return repriceFrozenMembershipSelection(offer, lines, allLines ?? []);
  }
  const normalizedAllLines = allLines ?? [];
  const totals = lines.reduce(
    (sum, line) => {
      const adjustment = membershipOfferAdjustmentForLine(offer, line, normalizedAllLines);
      const quotedTotal = Number(
        adjustment?.originalTotal ?? line.lineTotal ?? line.lineSubtotal ?? 0,
      );
      sum.nonMember += quotedTotal;
      sum.member += Number(adjustment?.memberTotal ?? quotedTotal);
      return sum;
    },
    { nonMember: 0, member: 0 },
  );
  const money = (value: number) => Math.round((Math.max(0, value) + Number.EPSILON) * 100) / 100;
  const nonMemberGrandTotal = money(totals.nonMember);
  const memberGrandTotal = money(totals.member);
  return {
    ...offer,
    nonMemberGrandTotal,
    memberGrandTotal,
    savings: money(nonMemberGrandTotal - memberGrandTotal),
  };
}

/** Match a frozen offer line to the estimate line without assuming catalog ids are unique. */
export function membershipOfferAdjustmentForLine(
  offer: EstimateMembershipOffer | null,
  line: EstimateLineItem,
  allLines: readonly EstimateLineItem[],
): EstimateMembershipOfferLineAdjustment | null {
  const adjustments = offer?.lineAdjustments ?? [];
  if (line.lineKey) {
    const keyed = adjustments.find((row) => row.lineKey === line.lineKey);
    if (keyed) return keyed;
  }
  const index = allLines.indexOf(line);
  if (index >= 0) {
    const positional = adjustments.find((row) => row.lineIndex === index);
    if (positional) return positional;
  }
  return null;
}

export function membershipOfferAdjustmentForLines(
  offer: EstimateMembershipOffer | null,
  lines: readonly EstimateLineItem[],
  allLines: readonly EstimateLineItem[],
): EstimateMembershipOfferLineAdjustment | null {
  const matched = lines
    .map((line) => membershipOfferAdjustmentForLine(offer, line, allLines))
    .filter((row): row is EstimateMembershipOfferLineAdjustment => row !== null);
  if (matched.length === 0) return null;
  // A rolled-up service may contain both discounted and unchanged component
  // lines. The frozen offer intentionally stores only lines whose member price
  // changed, but the displayed rollup still has to include every component in
  // both the original and member totals. Omitting unchanged lines understated
  // the whole bundle as soon as any sibling received a discount.
  const projected = lines.map((line) => {
    const adjustment = membershipOfferAdjustmentForLine(offer, line, allLines);
    if (adjustment) return adjustment;
    return {
      lineIndex: allLines.indexOf(line),
      lineKey: line.lineKey ?? null,
      lineItemId: line.lineItemId,
      originalSubtotal: Number(line.lineSubtotal ?? 0),
      memberSubtotal: Number(line.lineSubtotal ?? 0),
      originalTax: Number(line.lineTax ?? 0),
      memberTax: Number(line.lineTax ?? 0),
      originalTotal: Number(line.lineTotal ?? 0),
      memberTotal: Number(line.lineTotal ?? 0),
      savings: 0,
    } satisfies EstimateMembershipOfferLineAdjustment;
  });
  const sum = (key: keyof EstimateMembershipOfferLineAdjustment) =>
    Math.round(
      (projected.reduce((total, row) => total + Number(row[key] ?? 0), 0) + Number.EPSILON) * 100,
    ) / 100;
  return {
    lineIndex: matched[0].lineIndex,
    lineKey: matched[0].lineKey,
    lineItemId: matched[0].lineItemId,
    originalSubtotal: sum('originalSubtotal'),
    memberSubtotal: sum('memberSubtotal'),
    originalTax: sum('originalTax'),
    memberTax: sum('memberTax'),
    originalTotal: sum('originalTotal'),
    memberTotal: sum('memberTotal'),
    savings: sum('savings'),
  };
}

/** Frozen member-priced tax for the customer's currently selected lines. */
export function membershipOfferTaxTotal(
  offer: EstimateMembershipOffer | null,
  lines: readonly EstimateLineItem[],
  allLines: readonly EstimateLineItem[],
): number | null {
  if (!offer) return null;
  const total = lines.reduce((sum, line) => {
    const adjustment = membershipOfferAdjustmentForLine(offer, line, allLines);
    return sum + Number(adjustment?.memberTax ?? line.lineTax ?? 0);
  }, 0);
  return Math.round((total + Number.EPSILON) * 100) / 100;
}

/**
 * Project the exact frozen membership offer onto an estimate for pre-approval
 * rendering. This is deliberately presentation-only: the server still
 * revalidates the plan fingerprint and owns the authoritative approval write.
 *
 * The projected signature PDF is rendered before approval, but it is not
 * activated as the document of record until the server accepts and returns the
 * authoritative estimate. Applying the frozen line adjustments and plan price
 * here gives the signer the same commercial terms the endpoint then verifies.
 */
export function estimateWithAcceptedMembershipOffer(
  estimate: Estimate,
  offer: EstimateMembershipOffer,
): Estimate {
  if (offer.planPrice == null) return estimate;
  const planPrice = offer.planPrice;

  const money = (value: number) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
  const adjustedLines = estimate.lineItems.map((line) => {
    const adjustment = membershipOfferAdjustmentForLine(offer, line, estimate.lineItems);
    if (!adjustment) return line;
    return {
      ...line,
      lineSubtotal: money(adjustment.memberSubtotal),
      lineTax: money(adjustment.memberTax),
      lineTotal: money(adjustment.memberTotal),
    };
  });
  const offeredMembershipIndex = adjustedLines.findIndex(
    (line) =>
      isMembershipLine(line) && line.isOptional === true && line.membershipPlanId === offer.planId,
  );
  if (offeredMembershipIndex < 0) return estimate;
  const projectedLines = withMembershipSetupFees(
    adjustedLines.map((line, index) =>
      index === offeredMembershipIndex
        ? {
            ...line,
            basePrice: money(planPrice),
            membershipSetupFee: offer.setupFee ?? 0,
            quantity: 1,
            discountAmount: 0,
            discountPercent: 0,
            lineSubtotal: money(planPrice),
            lineTax: 0,
            lineTotal: money(planPrice),
            isOptional: true,
            isSelected: true,
          }
        : line,
    ),
  );
  const memberTax = money(projectedLines.reduce((sum, line) => sum + Number(line.lineTax ?? 0), 0));
  const memberSubtotal = money(
    projectedLines.reduce((sum, line) => sum + Number(line.lineSubtotal ?? 0), 0),
  );
  // An existing member accepting ANOTHER plan keeps the savings of the plan
  // they hold: the lines were already priced with it, and the offer's savings
  // and adjustments are the increment on top. The signed document therefore
  // reports both — cumulative total, both plan names, both sets of line
  // discounts. A non-member's held savings are 0 and this collapses to the
  // offer alone.
  const heldSnapshot = isMemberPricedSnapshot(estimate.membershipPlanSnapshot)
    ? estimate.membershipPlanSnapshot
    : null;
  const heldSavings = heldSnapshot ? money(estimate.membershipDiscountTotal ?? 0) : 0;
  const heldPlanName = heldSnapshot?.planName ?? null;
  const planName =
    heldPlanName && heldPlanName !== offer.planName
      ? `${heldPlanName} + ${offer.planName}`
      : offer.planName;
  const offerLineDiscounts = (offer.lineAdjustments ?? []).map((adjustment) => ({
    lineItemId: adjustment.lineItemId,
    type: 'MEMBERSHIP',
    originalAmount: money(adjustment.originalTotal),
    discountAmount: money(adjustment.savings),
    finalAmount: money(adjustment.memberTotal),
    discountLabel: `${offer.planName} member price`,
  }));

  return {
    ...estimate,
    lineItems: projectedLines,
    subtotal: memberSubtotal,
    taxTotal: memberTax,
    membershipDiscountTotal: money(heldSavings + offer.savings),
    depositEligibleTotal: money(offer.memberGrandTotal),
    grandTotal: money(offer.memberGrandTotal + offer.planPrice + (offer.setupFee ?? 0)),
    membershipPlanSnapshot: {
      ...(heldSnapshot ?? {}),
      planName,
      totalDiscount: money(heldSavings + offer.savings),
      lineDiscounts: [...(heldSnapshot?.lineDiscounts ?? []), ...offerLineDiscounts],
      offer: null,
    },
  };
}

/** Human-readable cadence for the exact billing option whose price is shown. */
export function membershipOfferPriceIntervalLabel(
  offer: Pick<EstimateMembershipOffer, 'planPriceInterval' | 'planPriceIntervalValue'> | null,
): string {
  if (!offer?.planPriceInterval) return '';
  const value = Math.max(1, Math.trunc(Number(offer.planPriceIntervalValue) || 1));
  const rawUnit = offer.planPriceInterval.toLowerCase();
  const singularUnit = rawUnit.replace(/s$/, '');
  return value === 1 ? `/${singularUnit}` : `/${value} ${rawUnit}`;
}
